// src/components/admin/settings/NotificationSettings.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bell } from 'lucide-react';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { updateNotificationSettings, selectNotificationSettings } from '../../../store/slices/platformSettingsSlice';
import toast from 'react-hot-toast';

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const notificationSettingsRedux = useSelector(selectNotificationSettings);

  const { translatedText: notificationSettings } = useTranslation('Notification Settings');
  const { translatedText: emailOnDonation } = useTranslation('Email on Donation Received');
  const { translatedText: adminEmailForDonations } = useTranslation('Admin Email for Donations');
  const { translatedText: weeklyReport } = useTranslation('Weekly Report');
  const { translatedText: sendDay } = useTranslation('Send Day');
  const { translatedText: recipientEmail } = useTranslation('Recipient Email');

  const [localSettings, setLocalSettings] = React.useState(
    notificationSettingsRedux || {
      donationReceived: { email: true, adminEmail: '' },
      weeklyReport: { enabled: true, sendDay: 1, recipientEmail: '' }
    }
  );

  React.useEffect(() => {
    if (notificationSettingsRedux) {
      setLocalSettings(notificationSettingsRedux);
    }
  }, [notificationSettingsRedux]);

  const handleNestedChange = (section, subsection, field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      await dispatch(updateNotificationSettings(localSettings)).unwrap();
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error(error || 'Failed to save settings');
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <Bell size={20} />
        {notificationSettings || 'Notification Settings'}
      </h3>
      <div className={styles.settingsForm}>
        <div className={styles.toggleGroup}>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localSettings.donationReceived.email}
              onChange={(e) => handleNestedChange('donationReceived', 'donationReceived', 'email', e.target.checked)}
            />
            {emailOnDonation || 'Email on Donation Received'}
          </label>
          <div className={styles.formGroup} style={{ marginLeft: '30px' }}>
            <label>{adminEmailForDonations || 'Admin Email for Donations'}</label>
            <input
              type="email"
              value={localSettings.donationReceived.adminEmail || ''}
              onChange={(e) => handleNestedChange('donationReceived', 'donationReceived', 'adminEmail', e.target.value)}
              placeholder="admin@example.com"
            />
          </div>
        </div>

        <div className={styles.toggleGroup}>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localSettings.weeklyReport.enabled}
              onChange={(e) => handleNestedChange('weeklyReport', 'weeklyReport', 'enabled', e.target.checked)}
            />
            {weeklyReport || 'Weekly Report'}
          </label>
          <div className={styles.formRow} style={{ marginLeft: '30px' }}>
            <div className={styles.formGroup}>
              <label>{sendDay || 'Send Day'}</label>
              <select
                value={localSettings.weeklyReport.sendDay}
                onChange={(e) => handleNestedChange('weeklyReport', 'weeklyReport', 'sendDay', Number(e.target.value))}
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>{recipientEmail || 'Recipient Email'}</label>
              <input
                type="email"
                value={localSettings.weeklyReport.recipientEmail || ''}
                onChange={(e) => handleNestedChange('weeklyReport', 'weeklyReport', 'recipientEmail', e.target.value)}
                placeholder="reports@example.com"
              />
            </div>
          </div>
        </div>
        <button className={styles.saveBtn} onClick={handleSave}>Save Changes</button>
      </div>
    </div>
  );
};

export default NotificationSettings;